import { Collapse, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import WhiteCard from "src/shared/components/WhiteCard";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { theme } from "src/assets/theme";
import ProductsList from "../ProductsList";
import { PatientOrder } from "src/api/types";
import { useGetOneTimeProductsBySlugQuery } from "src/api/services/productsApi";
import { skipToken } from "@reduxjs/toolkit/query";

interface Props {
  latestOrder: PatientOrder | undefined;
}

const AdditionalProductsCollapse = ({ latestOrder }: Props) => {
  const { t } = useTranslation();
  const component = "patient-dashboard.oneTimeProducts.additional";
  const [showMore, setShowMore] = useState<boolean>(false);

  const productSlug = useMemo(() => {
    if (latestOrder == null) return "tr-ot";

    return ["st-ot", "st"].includes(latestOrder.product.slug)
      ? "tr-ot"
      : "st-ot";
  }, [latestOrder]);

  const { data: monthly } = useGetOneTimeProductsBySlugQuery(
    showMore
      ? {
          slug: productSlug,
          number_of_months: 1,
        }
      : skipToken,
  );

  const { data: quarterly } = useGetOneTimeProductsBySlugQuery(
    showMore
      ? {
          slug: productSlug,
          number_of_months: 3,
        }
      : skipToken,
  );

  const monthlyProducts = useMemo(() => {
    if (monthly == null || monthly.length === 0) return [];

    return monthly;
  }, [monthly]);

  const quarterlyProducts = useMemo(() => {
    if (quarterly == null || quarterly.length === 0) return [];

    return quarterly;
  }, [quarterly]);

  const productName = useMemo(() => {
    if (latestOrder == null) return t(`medications.tirzepatide.capitalized`);

    if (["st-ot", "st"].includes(latestOrder.product.slug)) {
      return t(`medications.tirzepatide.capitalized`);
    }

    return t(`medications.semaglutide.capitalized`);
  }, [latestOrder, t]);

  return (
    <Stack gap={2} sx={{ order: 2 }}>
      <WhiteCard
        direction={"row"}
        justifyContent={"space-between"}
        onClick={() => setShowMore((o) => !o)}
        sx={{ cursor: "pointer" }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t(`${component}.header`, "See {{productName}} options below", {
            productName,
          })}
        </Typography>
        <Stack sx={{ color: theme.palette.primary.main }}>
          {showMore ? (
            <KeyboardArrowUp fontSize="large" />
          ) : (
            <KeyboardArrowDown fontSize="large" />
          )}
        </Stack>
      </WhiteCard>
      <Collapse in={showMore} timeout="auto" unmountOnExit>
        <Stack gap={2}>
          <ProductsList
            products={quarterlyProducts}
            productSlug={productSlug}
            numberOfMonths={3}
          />
          <ProductsList
            products={monthlyProducts}
            productSlug={productSlug}
            numberOfMonths={1}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default AdditionalProductsCollapse;
